import { webOtpHost } from '@/utils/constants/config'
import { useEffect, useRef } from 'react'

interface OTPCredential extends Credential {
  code: string
}

type Props = {
  onSuccess: (otp: string) => void
  allowedHost?: string
  codeLength?: number
  listenKey?: string | number
}

function isAllowedWebOtpHost(hostname: string, allowedHost: string) {
  return hostname === allowedHost || hostname.endsWith(`.${allowedHost}`)
}

function extractOtpCode(raw: string, codeLength: number) {
  const digits = raw.replace(/\D/g, '')
  if (digits.length >= codeLength) return digits.slice(0, codeLength)
  return ''
}

export default function useWebOtp({
  onSuccess,
  allowedHost = webOtpHost,
  codeLength = 4,
  listenKey = 0
}: Props): void {
  const onSuccessRef = useRef(onSuccess)
  onSuccessRef.current = onSuccess

  useEffect(() => {
    if (typeof window === 'undefined') return

    const hostname = window.location.hostname
    if (!isAllowedWebOtpHost(hostname, allowedHost)) return

    if (!('OTPCredential' in globalThis) || !('get' in navigator.credentials)) {
      return
    }

    const abortController = new AbortController()
    const options: CredentialRequestOptions & {
      otp: { transport: string[] }
    } = {
      otp: { transport: ['sms'] },
      signal: abortController.signal
    }

    navigator.credentials
      .get(options)
      .then((credential) => {
        const otp = credential as OTPCredential | null
        if (!otp?.code) return

        const code = extractOtpCode(otp.code, codeLength)
        if (code) onSuccessRef.current(code)
      })
      .catch((error: DOMException) => {
        if (error.name === 'AbortError') return
        console.warn('OTP retrieval failed:', error.name)
      })

    return () => abortController.abort()
  }, [allowedHost, codeLength, listenKey])
}
